import { useEffect, useState } from "react";
import { Facebook, Mail, Phone, Send, X } from "lucide-react";
import { CLIENT_SITES } from "../data/infoPages";

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
  }
}

const row =
  "flex items-center gap-4 p-4 rounded-xl glass hover:border-teal-500/50 transition-colors";
const field =
  "w-full px-4 py-3 rounded-xl bg-gray-800/50 border border-white/10 text-white placeholder-gray-400 focus:border-teal-500 transition-colors outline-hidden";

const Contact = () => {
  const [formState, setFormState] = useState({ name: "", email: "", phone: "", message: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [failed, setFailed] = useState(false);
  // Set by the "Build me one like this" links on the homepage showcase
  // (/contact.html?like=<client>). Read in an effect, not during render, so
  // the first client render still matches the prerendered HTML.
  const [likeSite, setLikeSite] = useState("");

  useEffect(() => {
    const like = new URLSearchParams(window.location.search).get("like");
    const site = CLIENT_SITES.find((s) => s.image === `/work/${like}.webp`);
    if (site) setLikeSite(site.name);
  }, []);

  const clearLikeSite = () => {
    setLikeSite("");
    window.history.replaceState(null, "", window.location.pathname);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setFailed(false);
    // A lead who thinks they reached us and didn't is the worst outcome: any failure has to say so.
    try {
      const response = await fetch("https://formspree.io/f/mykvgjdv", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          name: formState.name,
          email: formState.email,
          ...(formState.phone && { phone: formState.phone }),
          message: formState.message,
          ...(likeSite && { "wants a site like": likeSite }),
        }),
      });
      if (!response.ok) throw new Error(String(response.status));
      window.gtag?.("event", "generate_lead", { form_name: "contact" });
      setIsSubmitted(true);
      setFormState({ name: "", email: "", phone: "", message: "" });
      setTimeout(() => setIsSubmitted(false), 5000);
    } catch {
      setFailed(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormState((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  return (
    <section className="relative min-h-screen pt-32 pb-24">
      <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-12">
          <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight text-white mb-4">
            Let's build something rare
          </h1>
          <p className="text-xl text-gray-400 max-w-2xl">
            Rare Works is a small web design and marketing studio in Columbia, MS. Call, email, or send us a message.
          </p>
        </div>

        <div className="grid lg:grid-cols-[minmax(0,1fr)_320px] gap-6 items-start">
          <form onSubmit={handleSubmit} className="p-6 sm:p-8 rounded-2xl glass">
            <div className="space-y-5">
              {likeSite && (
                <div className="flex items-center justify-between gap-3 rounded-xl border border-teal-500/40 bg-teal-500/10 px-4 py-3 text-sm text-teal-100">
                  <span>You'd like a site like {likeSite}.</span>
                  <button
                    type="button"
                    onClick={clearLikeSite}
                    aria-label="Remove the example site"
                    className="text-teal-300 hover:text-white transition-colors"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              )}
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-2">
                    Name
                  </label>
                  <input type="text" id="name" name="name" value={formState.name} onChange={handleChange} required autoComplete="name" className={field} />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2">
                    Email
                  </label>
                  <input type="email" id="email" name="email" value={formState.email} onChange={handleChange} required autoComplete="email" className={field} />
                </div>
              </div>
              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-300 mb-2">
                  Phone <span className="font-normal text-gray-400">(optional)</span>
                </label>
                <input type="tel" id="phone" name="phone" value={formState.phone} onChange={handleChange} autoComplete="tel" className={field} />
              </div>
              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-300 mb-2">
                  Message
                </label>
                <textarea id="message" name="message" value={formState.message} onChange={handleChange} required rows={5} className={`${field} resize-none`} />
              </div>

              {failed && (
                <p role="alert" className="text-rose-400 text-sm">
                  Your message didn't send. Please try again, or call Alan at{" "}
                  <a href="tel:+16014419656" className="underline underline-offset-2">
                    (601) 441-9656
                  </a>
                  .
                </p>
              )}
              {isSubmitted && (
                <p role="status" className="text-emerald-400 text-sm">
                  Message sent. We'll get back to you soon.
                </p>
              )}

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full py-3.5 rounded-xl font-medium flex items-center justify-center gap-2 text-white bg-linear-to-r/srgb from-teal-500 to-cyan-600 hover:brightness-110 transition-[filter] disabled:opacity-70 disabled:cursor-not-allowed"
              >
                {isSubmitting ? "Sending..." : "Send Message"}
                {!isSubmitting && <Send className="w-4 h-4" />}
              </button>
            </div>
          </form>

          <div className="grid gap-3">
            <a href="tel:+16014419656" className={row}>
              <Phone className="w-5 h-5 text-gray-400 shrink-0" />
              <span>
                <span className="block text-sm text-gray-400">Call Alan</span>
                <span className="text-white font-medium tabular-nums">(601) 441-9656</span>
              </span>
            </a>
            <a href="tel:+17693781466" className={row}>
              <Phone className="w-5 h-5 text-gray-400 shrink-0" />
              <span>
                <span className="block text-sm text-gray-400">Call Kayla</span>
                <span className="text-white font-medium tabular-nums">(769) 378-1466</span>
              </span>
            </a>
            <a href="mailto:alan@rare-works.com" className={row}>
              <Mail className="w-5 h-5 text-gray-400 shrink-0" />
              <span className="min-w-0">
                <span className="block text-sm text-gray-400">Email us</span>
                <span className="text-white font-medium break-all">alan@rare-works.com</span>
              </span>
            </a>
            <a href="https://www.facebook.com/RareWorksStudio" target="_blank" rel="noopener noreferrer" className={row}>
              <Facebook className="w-5 h-5 text-gray-400 shrink-0" />
              <span>
                <span className="block text-sm text-gray-400">Follow us on Facebook</span>
                <span className="text-white font-medium">Rare Works Studio</span>
              </span>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Contact;
